/**
 * The unemployment claim (piece 34; decision 10651; T885): one claim a person
 * filed with a state about one of their employments, recorded by HR on the
 * employment's own page behind `hr.hire`. The response deadline is typed from
 * the state's notice and never computed; the contest packet is the Files tagged
 * to the claim under the Unemployment Claim Packet type.
 */
import { calendarDaysBetween } from '../documents/documentDate';
import { employeeRoute } from './employeeFile';

export const UNEMPLOYMENT_CONTEST_DECISIONS = ['Undecided', 'Contest', 'DoNotContest'] as const;
export type UnemploymentContestDecisionName = (typeof UNEMPLOYMENT_CONTEST_DECISIONS)[number];

export const UNEMPLOYMENT_CLAIM_OUTCOMES = ['Pending', 'Allowed', 'Denied', 'Withdrawn'] as const;
export type UnemploymentClaimOutcomeName = (typeof UNEMPLOYMENT_CLAIM_OUTCOMES)[number];

/** 🔒 The system file type a packet file is filed under, classified HR (decision 10651). */
export const UNEMPLOYMENT_CLAIM_FILE_TYPE_NAME = 'Unemployment Claim Packet';

/** The page's own address under the Employee page, and its builder. */
export const UNEMPLOYMENT_CLAIMS_ROUTE = '/hr/employees/:employmentId/unemployment-claims';
export function unemploymentClaimsRoute(employmentId: string): string {
  return `${employeeRoute(employmentId)}/unemployment-claims`;
}

/** The API's paths: the employment's claims, one claim's save, and its packet upload. */
export function unemploymentClaimsPath(employmentId: string): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}/unemployment-claims`;
}
export function unemploymentClaimPath(claimId: string): string {
  return `/hr/unemployment-claims/${encodeURIComponent(claimId)}`;
}
export function unemploymentClaimPacketPath(claimId: string): string {
  return `${unemploymentClaimPath(claimId)}/packet`;
}

/** 🔒 The refusal a viewer without `hr.hire` meets, the employee and their manager included. */
export const UNEMPLOYMENT_CLAIM_REFUSAL = 'Unemployment claims are recorded and read by HR alone; your role does not hold the hire permission.';

export interface UnemploymentClaimPacketFile {
  fileId: string;
  filename: string;
  uploadedAt: string;
}

export interface UnemploymentClaimRow {
  id: string;
  version: number;
  employmentId: string;
  stateCode: string;
  claimDate: string;
  responseDueOn: string;
  contestDecision: UnemploymentContestDecisionName;
  contestReason: string | null;
  outcome: UnemploymentClaimOutcomeName;
  outcomeOn: string | null;
  createdAt: string;
  createdByName: string;
  packet: UnemploymentClaimPacketFile[];
}

export interface UnemploymentClaimsAnswer {
  employmentId: string;
  personName: string;
  /** The Organization's own day, which the days left are counted from. */
  today: string;
  rows: UnemploymentClaimRow[];
}

export interface UnemploymentClaimDraft {
  stateCode: string;
  claimDate: string;
  responseDueOn: string;
}

export interface UnemploymentClaimUpdate {
  version: number;
  contestDecision: UnemploymentContestDecisionName;
  contestReason: string | null;
  outcome: UnemploymentClaimOutcomeName;
  outcomeOn: string | null;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const STATE = /^[A-Z]{2}$/;
const isDay = (value: unknown): value is string => typeof value === 'string' && DAY.test(value) && !Number.isNaN(new Date(`${value}T00:00:00Z`).getTime());

/** 🔨 A claim names its state, the date on the notice and the response deadline; a deadline before the notice is refused. */
export function unemploymentClaimProblem(draft: Partial<UnemploymentClaimDraft> | null | undefined): string | null {
  const stateCode = typeof draft?.stateCode === 'string' ? draft.stateCode.trim().toUpperCase() : '';
  if (!STATE.test(stateCode)) return 'A claim names the two-letter state it was filed in.';
  if (!isDay(draft?.claimDate)) return 'A claim carries the date on the state’s notice.';
  if (!isDay(draft?.responseDueOn)) return 'A claim carries the response deadline the notice names.';
  if (draft!.responseDueOn! < draft!.claimDate!) return 'The response deadline falls before the notice date.';
  return null;
}

/** 🔨 The decision and the outcome are one of their words, and a decided outcome carries its date. */
export function unemploymentClaimUpdateProblem(update: Partial<UnemploymentClaimUpdate> | null | undefined): string | null {
  if (!update || typeof update.version !== 'number') return 'A claim is saved from the version it was read at.';
  if (!(UNEMPLOYMENT_CONTEST_DECISIONS as readonly unknown[]).includes(update.contestDecision)) return 'The contest decision is Undecided, Contest or Do Not Contest.';
  if (!(UNEMPLOYMENT_CLAIM_OUTCOMES as readonly unknown[]).includes(update.outcome)) return 'The outcome is Pending, Allowed, Denied or Withdrawn.';
  if (update.outcome !== 'Pending' && !isDay(update.outcomeOn)) return 'A decided outcome carries the date it was decided.';
  if (update.outcome === 'Pending' && update.outcomeOn) return 'A pending outcome carries no date yet.';
  if (typeof update.contestReason === 'string' && update.contestReason.length > 2000) return 'The contest reason is at most 2,000 characters.';
  return null;
}

/** Days from today to the deadline; below zero is past it. */
export function claimDaysLeft(responseDueOn: string, today: string): number {
  return calendarDaysBetween(today, responseDueOn);
}

/** 🔒 A claim still Undecided on or past its deadline is drawn in the error colour (decision 10651). */
export function claimOverdue(row: Pick<UnemploymentClaimRow, 'responseDueOn' | 'contestDecision'>, today: string): boolean {
  return row.contestDecision === 'Undecided' && claimDaysLeft(row.responseDueOn, today) < 0;
}
